import { ButtonBase } from '@mui/material';
import FeedbackRenderer from 'components/Grading/FeedbackRenderer';
import FileViewer from 'components/Grading/FileViewer';
import LegacyFeedbackRenderer from 'components/Grading/LegacyFeedbackRenderer';
import RubricRenderer from 'components/Grading/RubricRenderer';
import Header from 'components/ViewSubmission/Header';
import {
    Box,
    Button,
    ButtonGroup,
    Container,
    Dropdown,
    Icon,
    IconButton,
    PDFViewer,
    SheetViewer,
    Stack,
    Text,
    styled,
} from 'shell/components';
import { getS3FileURL } from 'utils/helper';

const Mobile = ({
    currentAssignment,
    currentSubmission,
    selectedCriterionTitle,
    highlights,
    tab,
    setTab,
    selectedCriterionId,
    setSelectedCriterionId,
    selectedFeedbackId,
    setSelectedFeedbackId,
    isEditing,
    form,
    setForm,
    isDocumentFullScreen,
    setIsDocumentFullScreen,
    onClickEdit,
    onClickEditCancel,
    onClickEditSave,
    onSelect,
    updateFeedback,
}) => {
    return (
        <CustomContainer>
            <Header />
            <ContentStack>
                <TopStack isDocumentFullScreen={isDocumentFullScreen}>
                    {isDocumentFullScreen && (
                        <BannerBox>
                            <Text color={'inherit'}>
                                {tab === 'grades'
                                    ? `Currently viewing annotations for the criterion “${selectedCriterionTitle}.”`
                                    : `Currently viewing annotations for feedback.`}
                            </Text>
                        </BannerBox>
                    )}
                    {currentSubmission.data.type === 'application/pdf' ? (
                        <PDFViewer
                            highlights={highlights}
                            isFullScreen={isDocumentFullScreen}
                            url={getS3FileURL(currentSubmission.data)}
                        />
                    ) : [
                          'application/vnd.ms-excel',
                          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                          'text/csv',
                      ].includes(currentSubmission.data.type) ? (
                        <SheetViewer highlights={highlights} url={getS3FileURL(currentSubmission.data)} />
                    ) : (
                        <FileViewer
                            device={'mobile'}
                            docUrl={getS3FileURL(currentSubmission.data)}
                            fileType={currentSubmission.data.type}
                        />
                    )}
                </TopStack>
                <CenterButton onClick={() => setIsDocumentFullScreen((current) => !current)}>
                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                        <CustomText>{isDocumentFullScreen ? 'Show' : 'Hide'}</CustomText>
                        <ArrowIcon kind={isDocumentFullScreen ? 'arrowUp' : 'arrowDown'} />
                    </Stack>
                </CenterButton>
                <BottomStack isDocumentFullScreen={isDocumentFullScreen}>
                    <ControlsStack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                        <ButtonGroup
                            options={[
                                { key: 'grades', value: 'Grades' },
                                { key: 'feedback', value: 'Feedback' },
                            ]}
                            selected={tab}
                            onChange={setTab}
                        />
                        {tab === 'grades' && (
                            <>
                                {isEditing ? (
                                    <Stack direction={'row'} spacing={2}>
                                        <Button color={'neutral'} variant={'tertiary'} onClick={onClickEditCancel}>
                                            Cancel
                                        </Button>
                                        <Button onClick={onClickEditSave}>Save</Button>
                                    </Stack>
                                ) : (
                                    <Button
                                        color={'neutral'}
                                        startIcon={<Icon kind={'edit'} />}
                                        variant={'tertiary'}
                                        onClick={onClickEdit}
                                    >
                                        Edit Grades
                                    </Button>
                                )}
                            </>
                        )}
                        {tab === 'feedback' && currentSubmission.ai?.feedback?.feedback_type !== 'templated' && (
                            <Dropdown
                                id={'view-submission-more-options-dropdown'}
                                options={[
                                    { key: 'copy-all', value: 'Copy all feedback', icon: 'copy' },
                                    { key: 'delete', value: 'Delete all feedback', icon: 'delete' },
                                ]}
                                rootComponent={
                                    <IconButton
                                        aria-label={'more options'}
                                        kind={'moreHorizontal'}
                                        size={'medium'}
                                        variant={'square'}
                                    />
                                }
                                onSelect={onSelect}
                            />
                        )}
                    </ControlsStack>
                    {tab === 'grades' && (
                        <RubricRenderer
                            disabled={!isEditing}
                            showGraded={false}
                            assignment={currentAssignment}
                            currentSubmission={currentSubmission}
                            form={form}
                            setForm={setForm}
                            selectedCriterionId={selectedCriterionId}
                            setSelectedCriterionId={setSelectedCriterionId}
                            setShowFailedDialog={() => {}}
                        />
                    )}
                    {tab === 'feedback' && (
                        <Stack sx={{ flex: 1 }}>
                            {currentSubmission.is_failed || typeof currentSubmission.grading.feedback === 'string' ? (
                                <LegacyFeedbackRenderer
                                    currentSubmission={currentSubmission}
                                    form={form}
                                    setForm={setForm}
                                    setShowFailedDialog={() => {}}
                                />
                            ) : (
                                <FeedbackRenderer
                                    currentSubmission={currentSubmission}
                                    form={form}
                                    setForm={(func) => {
                                        setForm(func);
                                        updateFeedback(func(form));
                                    }}
                                    selectedFeedbackId={selectedFeedbackId}
                                    setSelectedFeedbackId={setSelectedFeedbackId}
                                />
                            )}
                        </Stack>
                    )}
                </BottomStack>
            </ContentStack>
        </CustomContainer>
    );
};

const CustomContainer = styled(Container)(({ theme }) => {
    return {
        position: 'absolute',
        top: 0,
        zIndex: theme.zIndex.drawer + 1,
        minHeight: '100%',
        padding: 0,
        paddingTop: '129px',
        background: theme.palette.ensightful.neutral[100],
    };
});

const ContentStack = styled(Stack)(() => {
    return {
        height: 'calc(100vh - 129px)',
        overflow: 'auto',
    };
});

const TopStack = styled(Stack)(({ isDocumentFullScreen }) => {
    return {
        position: 'relative',
        height: isDocumentFullScreen ? 'calc(100% - 36px)' : 'calc(45% - 36px)',
        transition: 'height 150ms',
    };
});

const BannerBox = styled(Box)(({ theme }) => {
    return {
        position: 'absolute',
        zIndex: 3,
        width: '100%',
        padding: theme.spacing(1, 5),
        background: theme.palette.ensightful.warning[200],
        color: theme.palette.ensightful.warning[900],
    };
});

const CenterButton = styled(ButtonBase)(({ theme }) => {
    return {
        height: '36px',
        background: theme.palette.ensightful.neutral[700],
        '&:focus-visible': {
            outline: `${theme.palette.ensightful.secondary[100]} solid 3px`,
            outlineOffset: '-5px',
        },
        '&:hover': {
            background: theme.palette.ensightful.neutral[800],
        },
        '&:active': {
            background: theme.palette.ensightful.neutral[900],
        },
    };
});

const ArrowIcon = styled(Icon)(({ theme }) => {
    return {
        color: theme.palette.ensightful.shades[0],
    };
});

const CustomText = styled(Text)(({ theme }) => {
    return {
        width: 'max-content',
        color: theme.palette.ensightful.shades[0],
    };
});

const BottomStack = styled(Stack)(({ theme, isDocumentFullScreen }) => {
    return {
        height: isDocumentFullScreen ? 0 : 'calc(55%)',
        background: theme.palette.ensightful.shades[0],
        overflow: 'auto',
        transition: 'height 150ms',
    };
});

const ControlsStack = styled(Stack)(({ theme }) => {
    return {
        padding: theme.spacing(3, 5),
    };
});

export default Mobile;
